import {
  SupabaseClient,
  createClient,
  type Session,
  type User,
} from '@supabase/supabase-js';
import { hashPassword } from './password';

// 从环境变量读取配置，如果未设置则使用默认值，默认为 PROD 配置
const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ||
  'https://nsuxehleffhqvhjlnmxg.supabase.co';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5zdXhlaGxlZmZocXZoamxubXhnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjMzODY3MDcsImV4cCI6MjA3ODk2MjcwN30.ialWk-oUCXP2Wvop2N7I_gNBt9n_8ZNnqtyEriqpevM';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file.',
  );
}

let supabaseClientInstance: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient => {
  if (!supabaseClientInstance) {
    // 创建 Supabase 客户端实例
    supabaseClientInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }
  return supabaseClientInstance;
};

// 用户信息类型
export interface UserInfo {
  id: string;
  email?: string;
  emailVerified?: boolean;
}

/**
 * 注册新用户（带邮箱验证）
 * @param email 邮箱地址
 * @param password 密码
 * @returns Promise<{ user: User | null; error: Error | null }>
 */
export const signUp = async (
  email: string,
  password: string,
): Promise<{ user: User | null; error: Error | null }> => {
  // 对密码进行加盐SHA256加密
  const hashedPassword = await hashPassword(password);

  const { data, error } = await getSupabaseClient().auth.signUp({
    email,
    password: hashedPassword,
    options: {
      emailRedirectTo: `${location.origin}/callback`,
    },
  });

  if (error) {
    return { user: null, error };
  }

  // 检查用户是否已存在：如果 identities 数组为空或不存在，说明用户已存在
  // 新注册的用户会有 identities 数组，已存在的用户可能返回空的 identities 数组
  if (
    data.user &&
    (!data.user.identities || data.user.identities.length === 0)
  ) {
    return {
      user: null,
      error: {
        message: 'User already registered',
        name: 'AuthApiError',
      } as any,
    };
  }

  return { user: data.user, error: null };
};

// 触发 Google 登录的函数
export async function signInWithGoogle() {
  const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
    provider: 'google',
    options: {
      // 登录成功后重定向回你的网站页面
      // 注意：这个 URL 必须在 Supabase 的 URL Configuration 中配置过
      redirectTo: `${location.origin}/callback`,
      // 可选：强制用户选择账号，即使已经登录
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  if (error) {
    console.error('Error signing in with Google:', error.message);
  }
}

// 触发 GitHub 登录的函数
export async function signInWithGitHub() {
  const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
    provider: 'github',
    options: {
      // 登录成功后重定向回你的网站页面
      // 注意：这个 URL 必须在 Supabase 的 URL Configuration 中配置过
      redirectTo: `${location.origin}/callback`,
    },
  });

  if (error) {
    console.error('Error signing in with GitHub:', error.message);
  }
}

/**
 * 登录
 * @param email 邮箱地址
 * @param password 密码
 * @returns Promise<{ session: Session | null; error: Error | null }>
 */
export const signIn = async (
  email: string,
  password: string,
): Promise<{ session: Session | null; error: Error | null }> => {
  // 对密码进行加盐SHA256加密
  const hashedPassword = await hashPassword(password);

  const { data, error } = await getSupabaseClient().auth.signInWithPassword({
    email,
    password: hashedPassword,
  });

  if (error) {
    return { session: null, error };
  }

  return { session: data.session, error: null };
};

/**
 * 登出
 */
export const signOut = async (): Promise<void> => {
  await getSupabaseClient().auth.signOut();
};

/**
 * 获取访问令牌
 * @returns Promise<string>
 */
export const getAccessToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await getSupabaseClient().auth.getSession();

  if (!session?.access_token) {
    throw new Error('No access token available');
  }

  return session.access_token;
};

/**
 * 获取用户信息
 * @returns Promise<UserInfo>
 */
export const getUserInfo = async (): Promise<UserInfo> => {
  const {
    data: { user },
  } = await getSupabaseClient().auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  return {
    id: user.id,
    email: user.email,
    emailVerified: user.email_confirmed_at !== null,
  };
};

/**
 * 检查用户是否已认证
 * @returns Promise<boolean>
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const {
    data: { session },
  } = await getSupabaseClient().auth.getSession();
  return !!session;
};

/**
 * 发送密码重置邮件
 * @param email 邮箱地址
 * @returns Promise<{ error: Error | null }>
 */
export const resetPassword = async (
  email: string,
): Promise<{ error: Error | null }> => {
  const { error } = await getSupabaseClient().auth.resetPasswordForEmail(
    email,
    {
      redirectTo: `${location.origin}/callback?type=recovery`,
    },
  );

  return { error };
};

/**
 * 更新密码（用于密码重置流程）
 * @param newPassword 新密码
 * @returns Promise<{ error: Error | null }>
 */
export const updatePassword = async (
  newPassword: string,
): Promise<{ error: Error | null }> => {
  // 对密码进行加盐SHA256加密
  const hashedPassword = await hashPassword(newPassword);

  const { error } = await getSupabaseClient().auth.updateUser({
    password: hashedPassword,
  });

  return { error };
};

/**
 * 重新发送验证邮件
 * @param email 邮箱地址
 * @returns Promise<{ error: Error | null }>
 */
export const resendVerificationEmail = async (
  email: string,
): Promise<{ error: Error | null }> => {
  const { error } = await getSupabaseClient().auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: `${location.origin}/callback`,
    },
  });

  return { error };
};

/**
 * 监听认证状态变化
 * @param callback 状态变化回调函数
 * @returns 取消监听的函数
 */
export const onAuthStateChange = (
  callback: (event: string, session: Session | null) => void,
) => {
  return getSupabaseClient().auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
};

/**
 * 检查用户是否需要重置密码
 * @param email 邮箱地址
 * @returns Promise<{ requirePasswordReset: boolean | null; error: Error | null }>
 */
export const checkRequirePasswordReset = async (
  email: string,
): Promise<{ requirePasswordReset: boolean | null; error: Error | null }> => {
  try {
    const { data, error } = await getSupabaseClient()
      .from('user_password_reset')
      .select('require_password_reset')
      .eq('email', email)
      .limit(1)
      .maybeSingle();

    if (error) {
      return { requirePasswordReset: null, error };
    }

    // 如果没有找到记录，返回 null
    if (data === null) {
      return { requirePasswordReset: null, error: null };
    }

    return { requirePasswordReset: data.require_password_reset, error: null };
  } catch (err: any) {
    return { requirePasswordReset: null, error: err };
  }
};

/**
 * 检查用户 ID 是否存在于 user_password_reset 表中
 * @param userId 用户 ID
 * @returns Promise<{ exists: boolean; error: Error | null }>
 */
export const checkUserPasswordResetExists = async (
  userId: string,
): Promise<{ exists: boolean; error: Error | null }> => {
  try {
    const { data, error } = await getSupabaseClient()
      .from('user_password_reset')
      .select('id')
      .eq('id', userId)
      .limit(1)
      .maybeSingle();

    if (error) {
      return { exists: false, error };
    }

    // 如果找到记录，返回 exists: true
    return { exists: data !== null, error: null };
  } catch (err: any) {
    return { exists: false, error: err };
  }
};

/**
 * 更新用户密码重置需求状态
 * @param userId 用户 ID
 * @param value 是否需要重置密码
 * @returns Promise<{ error: Error | null }>
 */
export const updateRequirePasswordReset = async (
  userId: string,
  value: boolean,
): Promise<{ error: Error | null }> => {
  try {
    const { error } = await getSupabaseClient()
      .from('user_password_reset')
      .update({ require_password_reset: value })
      .eq('id', userId);

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (err: any) {
    return { error: err };
  }
};
